import type { Project } from '../types'

/**
 * Detects duplicate project names and appends a year suffix to disambiguate.
 * - Unique name → displayName = name (unchanged)
 * - Duplicate name, different years → "Name (YYYY)"
 * - Duplicate name, same year → "Name (YYYY-<id4>)" using last 4 chars of project_id
 */
export function disambiguateProjectLabels<T extends Pick<Project, 'project_id' | 'name' | 'created_at'>>(
  projects: T[],
): (T & { displayName: string })[] {
  // Count how many times each name appears
  const nameCounts = new Map<string, number>()
  for (const p of projects) {
    nameCounts.set(p.name, (nameCounts.get(p.name) ?? 0) + 1)
  }

  // Among duplicates, detect same-name + same-year collisions
  const nameYearCounts = new Map<string, number>()
  for (const p of projects) {
    if ((nameCounts.get(p.name) ?? 0) > 1) {
      const year = p.created_at ? new Date(p.created_at).getFullYear().toString() : '????'
      const key = `${p.name}::${year}`
      nameYearCounts.set(key, (nameYearCounts.get(key) ?? 0) + 1)
    }
  }

  return projects.map((p) => {
    if ((nameCounts.get(p.name) ?? 0) <= 1) {
      return { ...p, displayName: p.name }
    }
    const year = p.created_at ? new Date(p.created_at).getFullYear().toString() : '????'
    const key = `${p.name}::${year}`
    if ((nameYearCounts.get(key) ?? 0) > 1) {
      // Same name, same year — append short project_id suffix
      const idSuffix = p.project_id.slice(-4)
      return { ...p, displayName: `${p.name} (${year}-${idSuffix})` }
    }
    return { ...p, displayName: `${p.name} (${year})` }
  })
}
